import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import { pxToRem } from 'utils/convert';
import { Skeleton } from 'ui/skeleton';
import { displayDateWithTodayAndYesterdayPrefix } from 'utils/date';
import { createAnimationOnAppear } from 'utils/styledComponentHelpers';

import { EventInfo } from '../../domain';
import { RECENT_EVENTS_ANIMATION_STAGGER_DELAY_MS, RECENT_EVENTS_ROW_COUNT } from '../constants';
import { EventIconBubble } from './EventIconBubble';
import { useEventDisplayName } from './hooks/useEventDisplayName';

const showEventOnMount = createAnimationOnAppear(
  'showEventOnMount',
  `opacity: 0; transform: translateY(10px);`,
  `opacity: 1; transform: translateY(0px);`,
);
const Wrapper = styled.li`
  ${showEventOnMount}
  ${new Array(RECENT_EVENTS_ROW_COUNT).fill(null).map(
    (_, i) => `
      &:nth-of-type(${i + 1}) {
        animation-delay: ${i * RECENT_EVENTS_ANIMATION_STAGGER_DELAY_MS}ms;
      }
    `,
  )}

  display: grid;
  grid-template-columns: ${pxToRem(30)} 1fr auto;
  grid-template-rows: auto;
  grid-column-gap: ${({ theme }) => theme.spacing.values.half};
  align-items: center;
  justify-items: start;
`;

const Name = styled(Link)`
  color: ${({ theme }) => theme.colors.textColors.textPrimary};
  font-size: ${({ theme }) => theme.styles.fontSizes.s14};
  padding: ${({ theme }) => theme.spacing.values.half};
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.linkColors.linkFocus};
  }
`;

const Timestamp = styled.div`
  color: ${({ theme }) => theme.colors.textColors.textSecondary};
  font-size: ${({ theme }) => theme.styles.fontSizes.s12};
`;

type EventProps = {
  event: EventInfo;
};

export const Event = ({ event }: EventProps) => {
  const { isLoading, displayName } = useEventDisplayName(event);

  return (
    <Wrapper>
      <EventIconBubble eventType={event.eventType} />
      {isLoading && <Skeleton data-test="recent-event-skeleton" />}
      {!isLoading && <Name to={`event/history?createdAt=${event.createdAt}`}>{displayName}</Name>}
      {!isLoading && (
        <Timestamp>{displayDateWithTodayAndYesterdayPrefix(event.createdAt)}</Timestamp>
      )}
    </Wrapper>
  );
};
